import { Search } from "@eai-toolkit-material-ui/icons";
import ClearIcon from "@mui/icons-material/Clear";
import { IconButton, InputBase, styled } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { SEARCH_DEBOUNCE_MS } from "@helpers/constants";
import { UUID_V4_REGEX } from "@helpers/uuid";

import { useDebounce } from "../../hooks/useDebounce";
import DescriptiveTooltip from "../DescriptiveTooltip";

const SearchInputWrapper = styled(InputBase)(({ theme }) => ({
  flexGrow: 1,
  background: "white",
  padding: theme.spacing(1),
  borderRadius: theme.spacing(0.5),
}));

const SearchIcon = styled(Search)(({ theme }) => ({
  marginRight: theme.spacing(2),
  marginLeft: theme.spacing(2),
  fontSize: theme.spacing(2),
}));

export type SearchFilterType = "id" | "name";

/**
 * Determines whether a search value should be treated as an ID search or name search
 */
export function inferSearchFilterType(value: string): SearchFilterType {
  return UUID_V4_REGEX.test(value) ? "id" : "name";
}

export interface SearchFilter {
  type?: SearchFilterType;
  value: string;
}

export interface NameIdSearchProps {
  /** The current search value */
  value: string;
  /** Callback when the search value changes, includes the detected filter type */
  onChange: (filter: SearchFilter) => void;
  /** When true, all searches are treated as ID searches */
  idOnly?: boolean; // In the future, we may want to have an enum to specify ID-only, name-only, or both
  /** Overrides the default placeholder text */
  placeholder?: string; // TODO: may want to delete this if it ends up being dead code
}

const NameIdSearch = React.memo(function NameIdSearch({
  value,
  onChange,
  idOnly = false,
  placeholder,
}: NameIdSearchProps) {
  // Display stays in local state: `value` round-trips through the URL, so
  // binding the input to it directly makes every keystroke wait on a full
  // route re-render (and lets stale commits revert typed characters).
  const [inputValue, setInputValue] = useState(value);

  // Emits once per typing pause rather than per keystroke, so each pause
  // costs one URL write + route render + API call instead of one per char.
  const [debouncedInput] = useDebounce(inputValue, SEARCH_DEBOUNCE_MS);

  // Track lastEmitted because the value we emit comes back as the `value`
  // prop after it round-trips through the URL — we want to skip those.
  // If we get a prop we never emitted, it's an external change (clear-all,
  // back/forward) and must overwrite the local text.
  const lastEmitted = useRef(value);

  // Always call the latest onChange: parent handlers close over their
  // queryState, so a stale one could resurrect filters changed while the
  // emit was pending.
  const onChangeRef = useRef(onChange);

  const idOnlyRef = useRef(idOnly);
  useEffect(() => {
    onChangeRef.current = onChange;
    idOnlyRef.current = idOnly;
  });

  const emit = useCallback((filterValue: string) => {
    lastEmitted.current = filterValue;
    onChangeRef.current({
      type: idOnlyRef.current ? "id" : inferSearchFilterType(filterValue),
      value: filterValue,
    });
  }, []);

  // lastEmitted already holds externally-adopted values too, so this also
  // swallows the debounced flush that follows an external change.
  useEffect(() => {
    if (debouncedInput !== lastEmitted.current) {
      emit(debouncedInput);
    }
  }, [debouncedInput, emit]);

  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setInputValue(value);
    }
  }, [value]);

  const handleSearchUpdate = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    [],
  );

  const handleClear = useCallback(() => {
    setInputValue("");
    emit("");
  }, [emit]);

  return (
    <SearchInputWrapper
      autoComplete="off"
      spellCheck="false"
      startAdornment={<SearchIcon color="secondary" />}
      placeholder={
        placeholder ?? (idOnly ? "Search by ID" : "Search by name or ID")
      }
      value={inputValue}
      onChange={handleSearchUpdate}
      endAdornment={
        inputValue ? (
          <DescriptiveTooltip title="Clear">
            <IconButton
              aria-label="Clear"
              onClick={handleClear}
              sx={{ p: 0 }}
              disableRipple
            >
              <ClearIcon color="secondary" />
            </IconButton>
          </DescriptiveTooltip>
        ) : (
          ""
        )
      }
    />
  );
});

export { NameIdSearch };
